/**
 * HUD hooks. None of them render per frame: the viewport and fullscreen
 * state change on resize / fullscreenchange, the zoom limits on zoomend, and
 * the arrival state three times per session at most.
 */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import type { MapController } from "@/contracts/map";
import type { Phase } from "@/contracts/store";
import { EASE } from "@/design";
import { bus, on } from "@/store/bus";
import { nextZoom, zoomLimits } from "./geometry";

/* ── viewport ───────────────────────────────────────────────────────── */

interface Viewport {
  w: number;
  h: number;
}
const SSR_VP: Viewport = { w: 1440, h: 900 };
let vp: Viewport = typeof window === "undefined" ? SSR_VP : { w: window.innerWidth, h: window.innerHeight };

function subscribeVp(cb: () => void): () => void {
  const f = () => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    if (w !== vp.w || h !== vp.h) {
      vp = { w, h };
      cb();
    }
  };
  f();
  window.addEventListener("resize", f);
  window.addEventListener("orientationchange", f);
  return () => {
    window.removeEventListener("resize", f);
    window.removeEventListener("orientationchange", f);
  };
}
const getVp = (): Viewport => vp;
const getSsrVp = (): Viewport => SSR_VP;

/** Window size in CSS px (stable object between resizes). */
export function useViewport(): Viewport {
  return useSyncExternalStore(subscribeVp, getVp, getSsrVp);
}

/* ── fullscreen ─────────────────────────────────────────────────────── */

type FsDoc = Document & { webkitFullscreenElement?: Element | null; webkitFullscreenEnabled?: boolean; webkitExitFullscreen?: () => void };
type FsEl = HTMLElement & { webkitRequestFullscreen?: () => void };

const fsElement = (): Element | null => (typeof document === "undefined" ? null : document.fullscreenElement ?? (document as FsDoc).webkitFullscreenElement ?? null);

function subscribeFs(cb: () => void): () => void {
  document.addEventListener("fullscreenchange", cb);
  document.addEventListener("webkitfullscreenchange", cb);
  return () => {
    document.removeEventListener("fullscreenchange", cb);
    document.removeEventListener("webkitfullscreenchange", cb);
  };
}
const getFs = (): boolean => !!fsElement();
const getFalse = (): boolean => false;

export function fullscreenSupported(): boolean {
  if (typeof document === "undefined") return false;
  return !!(document.fullscreenEnabled || (document as FsDoc).webkitFullscreenEnabled);
}

/** [isFullscreen, toggle] — the whole document goes full screen. */
export function useFullscreen(): [boolean, () => void] {
  const active = useSyncExternalStore(subscribeFs, getFs, getFalse);
  const toggle = useCallback(() => {
    const d = document as FsDoc;
    if (fsElement()) {
      if (d.exitFullscreen) void d.exitFullscreen().catch(() => {});
      else d.webkitExitFullscreen?.();
      return;
    }
    const el = document.documentElement as FsEl;
    if (el.requestFullscreen) void el.requestFullscreen().catch(() => {});
    else el.webkitRequestFullscreen?.();
  }, []);
  return [active, toggle];
}

/* ── zoom ───────────────────────────────────────────────────────────── */

export interface ZoomControl {
  atMin: boolean;
  atMax: boolean;
  ready: boolean;
  zoomBy(dir: 1 | -1): void;
}

/**
 * ± 1 zoom with the brand's settle curve (420 ms; 160 under reduced motion, §E.11),
 * about the current centre and padding. Emits camera:interrupt like every
 * other user camera input. The limit flags update on zoomend / moveend only.
 */
export function useZoomControl(controller: MapController | null, reduced: boolean): ZoomControl {
  const [lim, setLim] = useState({ atMin: false, atMax: false, ready: false });
  const pending = useRef<number | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    if (!controller) {
      setLim((l) => (l.ready ? { atMin: false, atMax: false, ready: false } : l));
      return undefined;
    }
    let alive = true;
    let detach: (() => void) | null = null;
    const attach = () => {
      if (!alive || detach) return;
      let map: ReturnType<MapController["map"]>;
      try {
        map = controller.map();
      } catch {
        return;
      }
      const update = () => {
        const next = zoomLimits(map.getZoom(), map.getMinZoom(), map.getMaxZoom());
        setLim((l) => (l.ready && l.atMin === next.atMin && l.atMax === next.atMax ? l : { ...next, ready: true }));
      };
      update();
      map.on("zoomend", update);
      map.on("moveend", update);
      detach = () => {
        map.off("zoomend", update);
        map.off("moveend", update);
      };
    };
    attach();
    void Promise.resolve(controller.ready)
      .then(attach)
      .catch(() => {});
    return () => {
      alive = false;
      detach?.();
      pending.current = null;
    };
  }, [controller]);

  const zoomBy = useCallback(
    (dir: 1 | -1) => {
      if (!controller) return;
      let map: ReturnType<MapController["map"]>;
      try {
        map = controller.map();
      } catch {
        return;
      }
      const target = nextZoom(map.getZoom(), pending.current, dir, map.getMinZoom(), map.getMaxZoom());
      if (target == null) return;
      pending.current = target;
      const token = ++seq.current;
      bus.emit("camera:interrupt", { reason: "user" });
      map.zoomTo(target, { duration: reduced ? 160 : 420, easing: EASE.settle }, { ld: "hud-zoom" });
      // Registered after zoomTo: stopping a previous ease fires its moveend synchronously.
      const done = () => {
        map.off("moveend", done);
        if (seq.current === token) pending.current = null;
      };
      map.on("moveend", done);
    },
    [controller, reduced],
  );

  return { atMin: lim.atMin, atMax: lim.atMax, ready: lim.ready, zoomBy };
}

/* ── UI arrival (§E.7 "ui" beat, §E.6 drive) ────────────────────────── */

export type Arrival = "hidden" | "arriving" | "shown";

const SHOWN_PHASES: ReadonlySet<Phase> = new Set<Phase>(["explore", "drive", "attract"]);
/** 640 settle + four staggers of 70 + a frame. */
const ARRIVAL_MS = 640 + 4 * 70 + 40;

/**
 * hidden during loading / intro; arriving (with the .ld-ui-in stagger) at the
 * intro's "ui" beat or when the phase first reaches explore; then shown.
 * stagger is false for the short intro (return visit, reduced motion, skip).
 */
export function useArrival(phase: Phase, reduced: boolean): { state: Arrival; stagger: boolean } {
  const [state, setStateRaw] = useState<Arrival>("hidden");
  const [stagger, setStagger] = useState(false);
  const stateRef = useRef<Arrival>("hidden");
  const beat = useRef(false);
  const set = useCallback((s: Arrival) => {
    stateRef.current = s;
    setStateRaw(s);
  }, []);

  const [beatSeen, setBeatSeen] = useState(false);
  useEffect(
    () =>
      on("intro:beat", ({ beat: b }) => {
        if (b !== "ui") return;
        beat.current = true;
        setBeatSeen(true);
      }),
    [],
  );

  useEffect(() => {
    if (phase === "loading") {
      beat.current = false;
      setBeatSeen(false);
    }
  }, [phase]);

  const visible = SHOWN_PHASES.has(phase) || (phase === "intro" && beatSeen);

  useEffect(() => {
    if (!visible) {
      if (stateRef.current !== "hidden") set("hidden");
      return;
    }
    if (stateRef.current !== "hidden") return;
    setStagger(beat.current && !reduced);
    set("arriving");
  }, [visible, reduced, set]);

  useEffect(() => {
    if (state !== "arriving") return undefined;
    const t = window.setTimeout(() => {
      if (stateRef.current === "arriving") set("shown");
    }, reduced ? 200 : ARRIVAL_MS);
    return () => window.clearTimeout(t);
  }, [state, reduced, set]);

  return { state, stagger };
}
